import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import {
  useArticleValue,
  useArticles,
  useCategories,
  useExternals,
  usePlatform,
  useTagValues,
} from '../ContextProviders/AppContext';
import './PlatformContentFrame.scss';
import { useLocation } from 'react-router-dom';
import { toasts } from '../../shared';
import { useLocalization } from '../ContextProviders/LocalizationContext';
import { useAuth } from '../ContextProviders/Auth';
import { LinkTypes, checkHyperLinkType } from '../Editor/LinkTypeChecker';
import { isValidUrl } from '../../util/DataHelpers';
import { useConcreteProject } from '../ContextProviders/ProjectContext';
import { getArticleTagValues } from '../../util/GetArticleTagValues';

interface IFrameData {
  articleId?: string | null;
  body: string;
  scrolling?: string;
  header?: boolean;
  bodyClass?: string;
  canBeUrl?: boolean;
  editTags?: boolean;
}
const template = (
  scriptSrcs: string[],
  styleHrefs: string[],
  body: string,
  injectHeader: boolean,
  bodyClass?: string,
): string => `
<!DOCTYPE HTML>
<html>
<head>
  <base target="_top">
  ${scriptSrcs.map((src) => `<script type="text/javascript" src="${src}"></script>`).join('\n')}
  ${styleHrefs.map((src) => `<link rel="stylesheet" type="text/css" href="${src}">`).join('\n')}
  <style>
  body:not(.edit-mode) [data-anchor-link] {
    visibility:hidden;
    width:0;
    pointer-events:none;
    overflow:hidden;
    display:inline-block;
  }
  [data-anchor-link] {
    cursor: pointer;
  }
  </style>
</head>
<body class="fr-view ${bodyClass ?? ''}">
    ${body}
    ${
      injectHeader
        ? `<script type="text/javascript">
    if (typeof setupArticle === "function") {
      setupArticle();
    }
    </script>`
        : ''
    }
</body>
</html>
`;

export const PlatformContentFrame = ({
  articleId = null,
  body,
  scrolling = 'yes',
  header = true,
  bodyClass,
  canBeUrl,
  editTags = false,
}: IFrameData): ReactElement => {
  const localization = useLocalization();
  const platform = usePlatform();
  const { projectJS, projectCSS, platformCSS, platformJS, froalaCSS } = platform.doc;
  const [element, setElement] = useState<HTMLIFrameElement>();
  const history = useHistory();
  const location = useLocation();
  const project = useConcreteProject();
  const articleValue = useArticleValue(articleId);

  const { docs: externals } = useExternals();
  const { docs: categories } = useCategories();
  const { docs: articles } = useArticles();
  const tagValuesDocs = useTagValues();

  const auth = useAuth();

  if (body.includes('pdf')) {
    body = body.replace(
      '<embed src="',
      '<iframe allowfullscreen src="https://mozilla.github.io/pdf.js/web/viewer.html?file=',
    );
  }

  if (articleId !== null && project.tags !== undefined && articleValue !== undefined && editTags) {
    if (project.tags.length > 0) {
      project.tags.forEach(function (tag) {
        if (articleValue[tag] !== undefined) {
          body = body.replace(`{{${tag}}}`, articleValue[tag]);
          body = body.replace(`{{${articleId}.${tag}}}`, articleValue[tag]);
        }
      });
    }
  }

  if (body.includes('data-show')) {
    const regexExtractAnchorTag = /<a\s+href="[^"]+"\s+data-show="article">.*?<\/a>/g;
    const regexExtractLink = /href="([^"]+)"/;
    const aTags = [...body.matchAll(regexExtractAnchorTag)];

    aTags.forEach((anchorTag) => {
      // eslint-disable-next-line
      const articleLink = anchorTag[0].match(regexExtractLink)![1];
      const articleId = articleLink.split('/');
      const articleToShow = articles.find((article) => article.fId === articleId.at(-1));

      if (articleToShow) {
        const escapedContent = escapeHTML(articleToShow.content);
        const articleTagValues = getArticleTagValues(articleToShow, tagValuesDocs);
        // eslint-disable-next-line
        const anchorEl = `<a href="${articleLink}">${anchorTag[0].match(/<a[^>]*>(.*?)<\/a>/)![1]}</a>`;
        const iframeEl = `
        <iframe
          style="width: 100%"
          onload="this.style.height=this.contentWindow.document.body.scrollHeight + 'px';"
          srcDoc='${template(
            [platformJS, projectJS],
            [platformCSS, projectCSS, froalaCSS],
            escapedContent,
            false,
            bodyClass,
          )}'>
          </iframe>`;
        const tagsEl =
          articleTagValues && articleTagValues.length > 0
            ? articleTagValues
                .map(
                  (tag) => `
                  <div style="border-radius: 20px; margin: 5px; border: 1px solid black; width: fit-content; padding: 2px 5px; background: #fff; ">
                    <span style="font-size: 12px">${tag}</span>
                  </div>`,
                )
                .join(' ')
            : '';

        body = body.replace(
          anchorTag[0],
          anchorEl + `<br/><div style="display: flex; flex-wrap: wrap;">${tagsEl}</div>` + iframeEl,
        );
      }
    });
  }

  const linkCallback = useCallback(
    (ev: Event) => {
      const target = ev.target as HTMLElement;

      const anchorAttribute = target.getAttribute('data-anchor-link');
      if (anchorAttribute) {
        void navigator.clipboard.writeText(anchorAttribute).then(() => {
          toasts.info(localization.strings.global.linkCopied);
        });
      }

      const link = target.closest('a')?.getAttributeNS(null, 'href');
      if (!link) return;
      ev.preventDefault();

      const url = new URL(link, window.origin);

      // scroll to hash on current page
      const locationId = url.pathname.substring(url.pathname.lastIndexOf('/') + 1);
      if (url.hash && document.location.pathname.includes(locationId)) {
        scrollToAnchorLink(url.hash);
        return;
      }

      if (document.location.hostname === url.hostname) {
        const linkURL = url.hash ? url.pathname + url.hash : url.pathname;
        if (onInterceptInternalLink(url)) {
          if (url.search) {
            const searchParam = url.search.substring(url.search.indexOf('=') + 1);
            sessionStorage.setItem('path', decodeURIComponent(searchParam));
            if (auth.accessWO === 'false') {
              toasts.warning(localization.strings.article.protectedArticle.errorMessageStaff);
            } else {
              history.push(linkURL);
            }
          } else {
            history.push(linkURL);
          }
        }
      } else {
        window.open(url.href);
      }
    },
    // eslint-disable-next-line
    [externals, categories, auth, articles],
  );

  const onInterceptInternalLink = (url: URL): boolean => {
    const theLinkType = checkHyperLinkType(url);
    const internalLinkID = url.pathname.substring(url.pathname.lastIndexOf('/') + 1);

    // Don't bother checking permissions for public static pages
    switch (theLinkType) {
      case LinkTypes.internalPolicy:
        return true;
      case LinkTypes.internalTerms:
        return true;
      case LinkTypes.internalAbout:
        return true;
    }

    // Check externals permission
    if (theLinkType === LinkTypes.internalExternal) {
      const canAccessExternal = externals.find((external) => external.fId === internalLinkID);

      if (!canAccessExternal && !auth.isStaff) {
        toasts.warning(localization.strings.article.protectedArticle.errorMessage);
        return false;
      } else if (!canAccessExternal && auth.isStaff) {
        toasts.warning(localization.strings.article.protectedArticle.errorMessageStaff);
        return false;
      } else {
        return true;
      }
    }

    // Check user permissions - Category link
    if (theLinkType === LinkTypes.internalCategory || theLinkType === LinkTypes.internalChiefCategory) {
      if (theLinkType === LinkTypes.internalChiefCategory && !auth.isAdmin) {
        toasts.warning(localization.strings.category.protectedCategory.chiefErrorMessage);
        return false;
      }

      const canAccessCategory =
        !categories.find((category) => category.fId === internalLinkID)?.isProtected ||
        auth.categoryPermissions.includes(internalLinkID) ||
        auth.isAdmin;

      if (!canAccessCategory) {
        toasts.warning(localization.strings.category.protectedCategory.defaultErrorMessage);
        return false;
      } else {
        return true;
      }
    }

    // Check user permissons - Article Link
    const canAccessArticle =
      articles.find((article) => article.fId === internalLinkID) ||
      (theLinkType === LinkTypes.internalChangelogArticle && auth.categoryPermissions.includes('changelog')) ||
      theLinkType === LinkTypes.settings ||
      theLinkType === LinkTypes.register ||
      theLinkType === LinkTypes.signIn ||
      auth.isAdmin;

    if (!canAccessArticle && !auth.isStaff) {
      toasts.warning(localization.strings.article.protectedArticle.errorMessage);
      return false;
    } else if (!canAccessArticle && auth.isStaff) {
      toasts.warning(localization.strings.article.protectedArticle.errorMessageStaff);
      return false;
    } else {
      return true;
    }
  };

  const scrollToAnchorLink = (targetId: string) => {
    const iframeContent = document.querySelector('iframe');
    const element = iframeContent?.contentDocument?.getElementById(targetId.replace('#', ''));

    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (element) {
      if (document.readyState === 'complete') {
        element.contentDocument?.addEventListener('click', linkCallback, false);
      }
      element.onload = () => {
        element.contentDocument?.addEventListener('click', linkCallback, false);
        if (location.hash) {
          scrollToAnchorLink(location.hash);
        }
      };

      return () => {
        element.contentDocument?.removeEventListener('click', linkCallback, false);
      };
    }
    return () => {
      /**/
    };
    // eslint-disable-next-line
  }, [element, linkCallback]);

  if (!platform.doc) return <></>;
  return (
    <iframe
      title="platform-content-iframe"
      id="platform-content-iframe"
      ref={(r) => {
        if (r) setElement(r);
      }}
      scrolling={scrolling}
      {...(canBeUrl && isValidUrl(body)
        ? { src: body }
        : { srcDoc: template([platformJS, projectJS], [platformCSS, projectCSS, froalaCSS], body, header, bodyClass) })}
    ></iframe>
  );
};

function escapeHTML(content) {
  return content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
